import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'react-toastify';
import { circlesDivisionsService, CircleDivision } from '../../services/api/circlesDivisionsService';

const CirclesDivisionsForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [parentOptions, setParentOptions] = useState<CircleDivision[]>([]);
  const [formData, setFormData] = useState({
    parent_id: null as number | null,
    circle_number: 0,
    name: '',
    designation: '',
    location: '',
    contact_number: '',
    email: '',
    level_type: 'superintending_engineer' as 'superintending_engineer' | 'executive_engineer',
    sub_number: '',
    is_fac: false,
    is_active: true,
    display_order: 0,
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    fetchParentOptions();
    if (isEditMode && id) {
      fetchRecord();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode]);

  const fetchParentOptions = async () => {
    try {
      const res = await circlesDivisionsService.getFlat();
      const parents = (res.data || []).filter((o) => o.level_type === 'superintending_engineer');
      setParentOptions(parents);
    } catch (error) {
      console.error('Failed to fetch parent options:', error);
    }
  };

  const fetchRecord = async () => {
    try {
      setLoading(true);
      const res = await circlesDivisionsService.getById(Number(id));
      if (!res.data) throw new Error('Record not found');
      const d = res.data;
      setFormData({
        parent_id: d.parent_id,
        circle_number: d.circle_number,
        name: d.name,
        designation: d.designation,
        location: d.location || '',
        contact_number: d.contact_number,
        email: d.email || '',
        level_type: d.level_type,
        sub_number: d.sub_number || '',
        is_fac: Boolean(d.is_fac),
        is_active: Boolean(d.is_active),
        display_order: d.display_order,
      });
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch record');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : type === 'number'
            ? Number(value)
            : value === ''
              ? name === 'parent_id'
                ? null
                : value
              : name === 'parent_id'
                ? Number(value)
                : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = (): boolean => {
    const errs: { [key: string]: string } = {};
    if (!formData.name.trim()) errs.name = 'Name is required';
    if (!formData.designation.trim()) errs.designation = 'Designation is required';
    if (!formData.contact_number.trim()) errs.contact_number = 'Contact number is required';
    if (!formData.circle_number) errs.circle_number = 'Circle number is required';
    if (formData.level_type === 'executive_engineer' && !formData.parent_id) {
      errs.parent_id = 'Parent (Superintending Engineer) is required for Executive Engineers';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please fix the errors');
      return;
    }
    try {
      setLoading(true);
      const submitData = {
        ...formData,
        parent_id: formData.level_type === 'superintending_engineer' ? null : formData.parent_id,
        email: formData.email.trim() || undefined,
        location: formData.location.trim() || undefined,
        sub_number: formData.sub_number.trim() || undefined,
      };
      if (isEditMode && id) {
        await circlesDivisionsService.update(Number(id), submitData);
        toast.success('Record updated successfully');
      } else {
        await circlesDivisionsService.create(submitData);
        toast.success('Record created successfully');
      }
      navigate('/circles-divisions');
    } catch (error) {
      toast.error(isEditMode ? 'Failed to update' : 'Failed to create');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {loading && isEditMode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-700">Loading...</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/circles-divisions')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Edit Officer' : 'Add New Officer'}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {isEditMode ? 'Update circle/division record' : 'Add a new circle/division record'}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="space-y-6">
          {/* Level Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Level Type <span className="text-red-500">*</span>
            </label>
            <select
              name="level_type"
              value={formData.level_type}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="superintending_engineer">Superintending Engineer</option>
              <option value="executive_engineer">Executive Engineer</option>
            </select>
          </div>

          {/* Parent (for EE) */}
          {formData.level_type === 'executive_engineer' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Parent (Superintending Engineer) <span className="text-red-500">*</span>
              </label>
              <select
                name="parent_id"
                value={formData.parent_id || ''}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.parent_id ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select parent...</option>
                {parentOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    Circle {p.circle_number} - {p.name}
                  </option>
                ))}
              </select>
              {errors.parent_id && <p className="mt-1 text-sm text-red-500">{errors.parent_id}</p>}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Circle Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Circle Number <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="circle_number"
                value={formData.circle_number}
                onChange={handleChange}
                min="1"
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.circle_number ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.circle_number && <p className="mt-1 text-sm text-red-500">{errors.circle_number}</p>}
            </div>

            {/* Sub Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sub Number</label>
              <input
                type="text"
                name="sub_number"
                value={formData.sub_number}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g. 1a, 2b"
              />
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Officer name"
            />
            {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
          </div>

          {/* Designation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Designation <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="designation"
              value={formData.designation}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.designation ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g. Superintending Engineer"
            />
            {errors.designation && <p className="mt-1 text-sm text-red-500">{errors.designation}</p>}
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g. Hyderabad"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Contact Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="contact_number"
                value={formData.contact_number}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.contact_number ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Phone number"
              />
              {errors.contact_number && <p className="mt-1 text-sm text-red-500">{errors.contact_number}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="email@example.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Display Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
              <input
                type="number"
                name="display_order"
                value={formData.display_order}
                onChange={handleChange}
                min="0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="mt-1 text-sm text-gray-500">Lower numbers appear first</p>
            </div>

            {/* Checkboxes */}
            <div className="space-y-4 pt-6">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_fac"
                  name="is_fac"
                  checked={formData.is_fac}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="is_fac" className="text-sm font-medium text-gray-700">
                  FAC Officer
                </label>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                  Active
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/circles-divisions')}
            className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CirclesDivisionsForm;
